import {datadogRum} from '@datadog/browser-rum';
import {hasOwnProperty} from '@one-click-lca/3d-buildings-shared/utils';
import * as Sentry from '@sentry/minimal';
import {AxiosError} from 'axios';
import {t} from 'i18next';
import {parse} from 'query-string';
import {toast} from 'react-toastify';

import {CHANGE_TYPE} from 'Pages/project/constants';
import {
  Project,
  TBalanceSheet,
  TContents,
  TDisplaySettings,
  TProjectGroup,
  TSceneInput,
  TView3D,
} from 'Pages/project/project_types';
import * as uploadcareUtils from 'Utils/uploadcare_utils';
import {
  DEFAULT_RATIOS,
  isCompleteRatios,
  RoofSpecification,
  type BaseRatio,
} from '~/constants';
import * as log from '~/logger';

import {TPartialProject} from './projectApi';
import {defaultProjectScreenshots} from './screenshots/constants';

export function getProjectHash(
  routerVersion: string | null,
  projectGroup: TProjectGroup | null,
): string {
  if (routerVersion) {
    return routerVersion;
  }

  if (!projectGroup) {
    return '';
  }

  const {calculated_key_child_hash: calculatedKeyChild, projects} =
    projectGroup;

  if (calculatedKeyChild && projects.some(v => v.hash === calculatedKeyChild)) {
    return calculatedKeyChild;
  }

  return projects[0].hash;
}

export function getRouterProjectHash(search: string): string {
  const routerParams = parse(search);
  const projectHash = routerParams.version as string | null;
  return projectHash || '';
}

export function getProjectView(search: string): number {
  const routerParams = parse(search);
  const routerView = parseInt(routerParams.view as string, 10);
  return Number.isFinite(routerView) ? routerView - 1 : 0;
}

export const getOtherQueryParams = (search: string): Record<string, number> => {
  const routerParams = parse(search);

  const autoResultsCount = parseInt(
    routerParams.autoResultsCount as string,
    10,
  );
  const autoResultsPercentileThreshold = parseInt(
    routerParams.autoResultsPercentileThreshold as string,
    10,
  );

  const otherQueryParams = {} as Record<string, number>;

  if (Number.isFinite(autoResultsCount)) {
    otherQueryParams.auto_results_count = autoResultsCount;
  }

  if (Number.isFinite(autoResultsPercentileThreshold)) {
    otherQueryParams.auto_results_percentile_threshold =
      autoResultsPercentileThreshold;
  }

  return otherQueryParams;
};

/**
 * This function is used in case a new attribute is added to the ratios
 * To avoid it marking it as optional, add it to the ratios saved in the project
 * so it's there for the rest of the code
 */
function migrateRatios(ratios: BaseRatio): void {
  for (const key of Object.keys(DEFAULT_RATIOS.global) as (keyof BaseRatio)[]) {
    if (ratios[key] === undefined) {
      Object.assign(ratios, {[key]: DEFAULT_RATIOS.global[key]});
    }
  }
}

export function migrateProject(project: Project): Project;
export function migrateProject(project: TPartialProject): TPartialProject;
export function migrateProject(project: TPartialProject): TPartialProject {
  if (project.contents) {
    const projectContent = JSON.parse(project.contents) as unknown;
    if (
      hasOwnProperty(projectContent, 'balanceSheet') &&
      hasOwnProperty(projectContent.balanceSheet, 'ratios') &&
      isCompleteRatios(projectContent.balanceSheet.ratios)
    ) {
      const {ratios} = projectContent.balanceSheet;
      for (const tenementRatios of Object.values(ratios.tenements)) {
        if (
          hasOwnProperty(tenementRatios, 'default_max_building_depth') &&
          tenementRatios.default_max_building_depth === false &&
          !hasOwnProperty(tenementRatios, 'max_building_depth')
        ) {
          // if default_max_building_depth was disabled by the user in a project before
          // it was replaced with max_building_depth then set max_building_depth to null
          Object.assign(tenementRatios, {max_building_depth: null});
        }
        for (const key of [
          'max_building_width',
          'max_building_height',
          'max_building_depth',
          'max_facade_length',
        ] as const) {
          if (
            hasOwnProperty(tenementRatios, key) &&
            !Number.isFinite(tenementRatios[key]) &&
            tenementRatios[key] !== null
          ) {
            Object.assign(tenementRatios, {[key]: DEFAULT_RATIOS.global[key]});
          }
        }
        if (
          hasOwnProperty(tenementRatios, 'default_hasRoof') &&
          tenementRatios.default_hasRoof === true
        ) {
          tenementRatios.roofSpecification = RoofSpecification.hasSlopedRoof;
          delete tenementRatios.default_hasRoof;
        }
      }

      //generic ratios migration needs to be done AFTER specific migration
      migrateRatios(ratios.global);

      project.contents = JSON.stringify(projectContent);
    }
  }
  //TODO 2024/04/18 temporary while waiting for the attribute to be added on the backend
  //To be removed after a few weeks if the modification has been done on the backend
  if (project.project_rules) {
    for (const rules of project.project_rules) {
      rules.isCustom = true;
    }
  }
  if (!project.forced_zoning || !project.idpars) {
    return project;
  }

  let forcedZoning = null;
  try {
    forcedZoning = JSON.parse(project.forced_zoning);
  } catch (e) {
    console.log('Error parsing forced zoning : ' + e);
  }
  if (!forcedZoning) {
    project.forced_zoning = JSON.stringify({});
  } else if (forcedZoning.insee_code) {
    //forced zoning pre multi-tenement : only one force zoning instead of one per tenement
    //convert to multi-tenement format
    const {
      organization,
      insee_code,
      zone,
      blessed,
      author_hash,
      version: zoningVersion,
    } = forcedZoning;
    const tenementId = project.idpars.replace(/,/g, '-');
    const newForcedZoning = {
      [tenementId]: {
        author_hash,
        organization,
        insee_codes: insee_code.split(','),
        zone,
        blessed,
        isCustom: false,
        version: zoningVersion,
      },
    };
    project.forced_zoning = JSON.stringify(newForcedZoning);
  }
  return project;
}

export async function save3D(
  project: Project,
  projectGroup: TProjectGroup,
  hasCustomBuildings3D: () => boolean,
  getMapScreenshot3D: () => string | null,
  getMapOrthoScreenshot: () => string | null,
  views: TView3D[],
  currentViewIndex: number,
  getSceneInput: () => TSceneInput,
  contents: string,
  balanceSheet: TBalanceSheet,
  updateVersion: (
    fields: Partial<Project>,
    changeType?: number,
  ) => Promise<void>,
): Promise<void> {
  const startTime = new Date();
  const hasCustomBuildings = hasCustomBuildings3D();
  const projectScreenshotData =
    !project?.image && hasCustomBuildings ? getMapScreenshot3D() : null;
  let projectOrthoVisual = null;
  try {
    projectOrthoVisual = getMapOrthoScreenshot();
  } catch (e) {
    log.warning("Couldn't generate ortho visual for project", {
      error: e,
      projectHash: projectGroup.hash,
      versionHash: project.hash,
    });
  }

  const sceneInput = getSceneInput();
  const newContents = getCurrentContents(contents, getSceneInput, balanceSheet);
  const currentView = getCurrentView(views, currentViewIndex);

  const updatedView = await updateViewData(
    currentView,
    sceneInput.displaySettings,
    projectScreenshotData,
    projectOrthoVisual,
  );

  const newView = {
    ...updatedView,
  };
  const newViews = [...views];
  newViews[currentViewIndex] = newView;
  const {image, ortho_preview_uploadcare} = project;
  const {screenshot, orthoScreenshot} = updatedView;
  const versionInput = {
    contents: JSON.stringify(newContents),
    views: JSON.stringify(newViews),
    image: image || screenshot,
    ortho_preview_uploadcare: orthoScreenshot || ortho_preview_uploadcare,
    ...defaultProjectScreenshots,
  };
  await updateVersion(versionInput, CHANGE_TYPE['3D'] | CHANGE_TYPE.DATA);

  const finishTime = new Date();
  datadogRum.addAction('modifyBuilding', {
    modifyBuilding: Number(finishTime) - Number(startTime),
  });
}

export function getCurrentContents(
  contents: string,
  getSceneInput: () => TSceneInput,
  balanceSheet: TBalanceSheet,
): TContents {
  const sceneInput = getSceneInput();
  return {
    ...JSON.parse(contents),
    entities: sceneInput?.entities,
    balanceSheet,
  };
}

function getCurrentView(views: TView3D[], currentViewIndex: number) {
  if (views && currentViewIndex < views.length) {
    return views[currentViewIndex];
  }
}

function updateViewData(
  view: TView3D | undefined,
  displaySettings: TDisplaySettings,
  screenshotData: string | null,
  orthoScreenshotData: string | null,
) {
  let screenshotPromise = Promise.resolve('');
  if (screenshotData) {
    screenshotPromise = uploadcareUtils.uploadScreenshot(screenshotData);
  }
  let orthoPromise = Promise.resolve('');
  if (orthoScreenshotData) {
    orthoPromise = uploadcareUtils.uploadScreenshot(orthoScreenshotData);
  }
  return Promise.all([screenshotPromise, orthoPromise])
    .then(values => {
      const updatedData = {
        ...view,
        displaySettings,
      };
      if (values[0]) {
        updatedData.screenshot = values[0];
      }
      if (values[1]) {
        updatedData.orthoScreenshot = values[1];
      }
      return updatedData;
    })
    .catch(error => {
      log.exception(error);
      return {
        ...view,
        displaySettings,
      };
    });
}

export function getCurrentViewIndex(
  views: TView3D[],
  viewIndex: number,
): number {
  return Math.max(0, Math.min(views.length - 1, viewIndex));
}

export function displayPluAutoError(error: unknown): void {
  const [errorKey, isExpectedError] = (() => {
    if (error instanceof AxiosError) {
      if (error.response?.status === 403) {
        return [error.response.data.detail, true];
      }
    }
    return ['An error occured, contact our team', false];
  })();

  toast.warn(t(errorKey), {autoClose: 5000});

  if (!isExpectedError) {
    Sentry.captureException(error);
  }
}
